/**
 * Business form section and field configuration.
 * Keys and paths aligned with backend business_forms schema. Form name is set in the "new business form" popup, not as a section.
 * Required fields: Type of Business, Name of business owner, Principal business, Business name, Business address, Accounting method;
 * Partnership (1065/1120-S): Number of partners, Partner information (Name, Address, SSN, Partnership %).
 */

export const BUSINESS_SECTION_KEYS = [
  'businessDetails',
  'partnershipDetails',
  'income',
  'expenses',
  'costOfGoodsSold',
  'vehicleInfo',
  'financialInfo',
] as const;

export type BusinessSectionKey = (typeof BUSINESS_SECTION_KEYS)[number];

export interface SectionMeta {
  key: BusinessSectionKey;
  title: string;
  description?: string;
}

export interface FieldMeta {
  key: string;
  label: string;
  placeholder?: string;
  type?: 'text' | 'number' | 'select' | 'checkbox' | 'date' | 'stateSelect';
  fullWidth?: boolean;
  optionsKey?: string;
  /** Field is required for business forms */
  required?: boolean;
  /** Show section/field only when businessDetails.businessType is one of these (e.g. PARTNERSHIP, S_CORP) */
  showWhenBusinessType?: string[];
  /** For date fields: use 'monthYear' to show MM/yyyy only (e.g. placed in service). Default is full date MM/dd/yyyy. */
  dateFormat?: 'date' | 'monthYear';
}

export const BUSINESS_SECTIONS: SectionMeta[] = [
  {
    key: 'businessDetails',
    title: 'Business Details',
    description: 'Type of Business (LLC/S-Corp/C-Corp/Partnership etc.), Name of business owner, Taxpayer ID, Principal business or profession, Business Name, Business Address, Accounting method (Cash/Accrual), Materially participate (Yes/No), Started or acquired this year',
  },
  {
    key: 'partnershipDetails',
    title: 'Partnership Details',
    description: 'Required when business is Partnership (1065) or S-Corp (1120-S). Number of partners and for each partner: Name, Address, SSN, Partnership percentage.',
  },
  { key: 'income', title: 'Income Info', description: 'Gross receipts or sales, Returns and allowances' },
  { key: 'expenses', title: 'Expenses', description: 'Advertisement, Car and truck, Commissions and fees, Depreciation, Employee benefits, Insurance, Interest/Mortgage, Legal and professional, Office expense, Pension and profit-sharing, Rent or lease, Repairs and maintenance, Supplies, Taxes and licenses, Utilities, Wages, Other expenses' },
  { key: 'costOfGoodsSold', title: 'Cost of Goods Sold', description: 'Inventory at beginning of year, Purchases less cost of items withdrawn for personal use, Cost of labor, Materials and supplies, Other costs, Inventory at end of year' },
  { key: 'vehicleInfo', title: 'Information on Your Vehicle', description: 'Complete only if claiming car or truck expenses: Placed in service date, Total miles/Business miles, Vehicle available for personal use, Another vehicle available, Evidence to support deduction' },
  { key: 'financialInfo', title: 'Financial Information', description: 'Bank Balance on 12/31, Any Other details' },
];

export const BUSINESS_FIELDS: Record<BusinessSectionKey, FieldMeta[]> = {
  businessDetails: [
    { key: 'businessType', label: 'Type of Business', type: 'select', optionsKey: 'BUSINESS_TYPE', required: true },
    { key: 'proprietorName', label: 'Name of the Business Owner', placeholder: 'Business Owner Name', required: true },
    { key: 'taxpayerId.idType', label: 'Taxpayer ID Type', type: 'select', optionsKey: 'TAXPAYER_ID_TYPE' },
    { key: 'taxpayerId.idValue', label: 'Taxpayer ID Value', placeholder: 'SSN Or EIN' },
    { key: 'principalBusiness', label: 'Principal Business or Profession', placeholder: 'Principal Business Activity', fullWidth: true, required: true },
    { key: 'businessName', label: 'Business Name', placeholder: 'Business Name', fullWidth: true, required: true },
    { key: 'businessAddress.street', label: 'Business Address — Street', placeholder: 'Street', fullWidth: true, required: true },
    { key: 'businessAddress.city', label: 'Business Address — City', placeholder: 'City', required: true },
    { key: 'businessAddress.state', label: 'Business Address — State', type: 'stateSelect', required: true },
    { key: 'businessAddress.zip', label: 'Business Address — ZIP', placeholder: 'ZIP', required: true },
    { key: 'accountingMethod', label: 'Accounting Method', type: 'select', optionsKey: 'ACCOUNTING_METHOD', required: true },
    { key: 'materiallyParticipated', label: 'Materially Participated', type: 'checkbox', fullWidth: true },
    { key: 'startedThisYear', label: 'Started This Year', type: 'checkbox' },
    { key: 'startDate', label: 'Start Date (MM/DD/YYYY)', placeholder: 'MM/DD/YYYY', type: 'date' },
  ],
  partnershipDetails: [
    { key: 'numberOfPartners', label: 'Number of Partners', type: 'text', placeholder: '0', required: true, showWhenBusinessType: ['PARTNERSHIP', 'S_CORP'] },
    // Partner template fields (used for each partner 0..numberOfPartners-1 in UI)
    { key: 'partnerInformation.0.name', label: 'Partner — Name', placeholder: 'Partner Name', fullWidth: true, showWhenBusinessType: ['PARTNERSHIP', 'S_CORP'] },
    { key: 'partnerInformation.0.address.street', label: 'Partner — Street', placeholder: 'Street', fullWidth: true, showWhenBusinessType: ['PARTNERSHIP', 'S_CORP'] },
    { key: 'partnerInformation.0.address.city', label: 'Partner — City', placeholder: 'City', showWhenBusinessType: ['PARTNERSHIP', 'S_CORP'] },
    { key: 'partnerInformation.0.address.state', label: 'Partner — State', type: 'stateSelect', showWhenBusinessType: ['PARTNERSHIP', 'S_CORP'] },
    { key: 'partnerInformation.0.address.zip', label: 'Partner — ZIP', placeholder: 'ZIP', showWhenBusinessType: ['PARTNERSHIP', 'S_CORP'] },
    { key: 'partnerInformation.0.ssn', label: 'Partner — SSN', placeholder: 'SSN', showWhenBusinessType: ['PARTNERSHIP', 'S_CORP'] },
    { key: 'partnerInformation.0.partnershipPercentage', label: 'Partner — Partnership %', type: 'text', placeholder: '0', showWhenBusinessType: ['PARTNERSHIP', 'S_CORP'] },
  ],
  income: [
    { key: 'grossReceipts', label: 'Gross Receipts', placeholder: 'Gross Receipts', type: 'text' },
    { key: 'returnsAndAllowances', label: 'Returns And Allowances', placeholder: 'Returns And Allowances', type: 'text' },
  ],
  expenses: [
    { key: 'advertising', label: 'Advertising', type: 'text', placeholder: '0' },
    { key: 'carAndTruck', label: 'Car And Truck', type: 'text', placeholder: '0' },
    { key: 'commissionsAndFees', label: 'Commissions And Fees', type: 'text', placeholder: '0' },
    { key: 'depreciation', label: 'Depreciation', type: 'text', placeholder: '0' },
    { key: 'employeeBenefits', label: 'Employee Benefits', type: 'text', placeholder: '0' },
    { key: 'insurance', label: 'Insurance', type: 'text', placeholder: '0' },
    { key: 'interestMortgage', label: 'Interest Mortgage', type: 'text', placeholder: '0' },
    { key: 'interestOther', label: 'Interest Other', type: 'text', placeholder: '0' },
    { key: 'legalAndProfessional', label: 'Legal And Professional', type: 'text', placeholder: '0' },
    { key: 'officeExpense', label: 'Office Expense', type: 'text', placeholder: '0' },
    { key: 'pensionProfitSharing', label: 'Pension Profit Sharing', type: 'text', placeholder: '0' },
    { key: 'rentVehicles', label: 'Rent Vehicles', type: 'text', placeholder: '0' },
    { key: 'rentOther', label: 'Rent Other', type: 'text', placeholder: '0' },
    { key: 'repairsAndMaintenance', label: 'Repairs And Maintenance', type: 'text', placeholder: '0' },
    { key: 'supplies', label: 'Supplies', type: 'text', placeholder: '0' },
    { key: 'taxesAndLicenses', label: 'Taxes And Licenses', type: 'text', placeholder: '0' },
    { key: 'utilities', label: 'Utilities', type: 'text', placeholder: '0' },
    { key: 'wages', label: 'Wages', type: 'text', placeholder: '0' },
  ],
  costOfGoodsSold: [
    { key: 'beginningInventory', label: 'Beginning Inventory', type: 'text', placeholder: '0' },
    { key: 'purchases', label: 'Purchases', type: 'text', placeholder: '0' },
    { key: 'costOfLabor', label: 'Cost Of Labor', type: 'text', placeholder: '0' },
    { key: 'materialsAndSupplies', label: 'Materials And Supplies', type: 'text', placeholder: '0' },
    { key: 'otherCosts', label: 'Other Costs', type: 'text', placeholder: '0' },
    { key: 'endingInventory', label: 'Ending Inventory', type: 'text', placeholder: '0' },
  ],
  vehicleInfo: [
    { key: 'hasVehicleExpenses', label: 'Has Vehicle Expenses', type: 'checkbox', fullWidth: true },
    { key: 'placedInServiceDate', label: 'Placed In Service Date (MM/YYYY)', placeholder: 'MM/YYYY', type: 'date', dateFormat: 'monthYear' },
    { key: 'totalMiles', label: 'Total Miles', placeholder: 'Total Miles', type: 'text' },
    { key: 'businessMiles', label: 'Business Miles', placeholder: 'Business Miles', type: 'text' },
    { key: 'personalMiles', label: 'Personal Miles', placeholder: 'Personal Miles', type: 'text' },
    { key: 'vehicleAvailableForPersonalUse', label: 'Vehicle Available For Personal Use', type: 'checkbox' },
    { key: 'anotherVehicleAvailable', label: 'Another Vehicle Available', type: 'checkbox' },
    { key: 'hasSupportingEvidence', label: 'Has Supporting Evidence', type: 'checkbox' },
    { key: 'mileageMethod', label: 'Mileage Method', type: 'select', optionsKey: 'MILEAGE_METHOD' },
  ],
  financialInfo: [
    { key: 'yearEndBankBalance', label: 'Year-End Bank Balance', placeholder: 'Year-End Bank Balance', type: 'text' },
    { key: 'additionalNotes', label: 'Additional Notes', placeholder: 'Additional Notes', fullWidth: true },
  ],
};

/**
 * Returns business form config, optionally keyed by formName for future per-form variations.
 * Schema: formName is a top-level string on business_forms (default null).
 */
export function getBusinessFormConfig(_formName?: string | null) {
  return {
    sectionKeys: BUSINESS_SECTION_KEYS,
    sections: BUSINESS_SECTIONS,
    fields: BUSINESS_FIELDS,
  };
}
